import { LogisticPivotMain } from '@modules/Reports/LogisticPivot';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Separator } from '@shared/components/ui/shadcn/separator';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useToast } from '@shared/hooks/use-toast';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogisticsData } from '../../hooks/useLogisticsData';
import type { ProductMetric } from '../../models/LogisticsApiResponse';
import { LogisticsFilters } from '../../models/LogisticsFilters';
import { ProductStatistics } from '../../models/ProductStatistics';
import { LogisticsMetrics, OrdersChartsSection, ProductStatisticsSection } from '../display';
import { LogisticsFiltersBar } from '../interaction';
import { LogisticsSkeleton } from '../skeletons';

/**
 * Componente contenedor principal del módulo Logistics
 * Maneja la lógica de estado y datos del módulo
 * Main container component for Logistics module - Handles state and data logic
 */
export const Main = () => {
  const { t } = useTranslation('logisticsModule');
  const { toast } = useToast();

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  // State to control that initial loading spinner is shown only once
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Ref para rastrear el último error mostrado y evitar mostrar el toast múltiples veces
  // Ref to track the last error shown and avoid showing toast multiple times
  const lastErrorShown = useRef<string | null>(null);

  // Helper para formatear fecha sin problemas de zona horaria
  // Helper to format date without timezone issues
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Valores por defecto para fechas
  // Default values for dates
  const getDefaultStartDate = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return formatDateLocal(thirtyDaysAgo);
  };

  const getDefaultEndDate = () => {
    const today = new Date();
    return formatDateLocal(today);
  };

  // Query params para sincronizar búsqueda y filtros
  // Query params to sync search and filters
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    start_date: { type: 'string', default: getDefaultStartDate() },
    end_date: { type: 'string', default: getDefaultEndDate() },
    date_column: {
      type: 'string',
      default: 'sent_at' as 'processed_at' | 'created_at' | 'updated_at' | 'sent_at',
    },
    payment_method_id: { type: 'number', default: null },
    shop_id: { type: 'number', default: null },
    courier_id: { type: 'number', default: null },
  });

  // Estado de búsqueda sincronizado con la URL
  // Search state synced with the URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');

  // Estados para filtros de la barra superior sincronizados con queryParams
  // States for top bar filters synced with queryParams
  const [startDate, setStartDate] = useState<string | undefined>(queryParams.start_date ?? getDefaultStartDate());
  const [endDate, setEndDate] = useState<string | undefined>(queryParams.end_date ?? getDefaultEndDate());
  const [dateColumn, setDateColumn] = useState<'processed_at' | 'created_at' | 'updated_at' | 'sent_at'>(
    (queryParams.date_column as typeof dateColumn) ?? 'sent_at',
  );
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(queryParams.payment_method_id ?? null);
  const [shopId, setShopId] = useState<number | null>(queryParams.shop_id ?? null);
  const [courierId, setCourierId] = useState<number | null>(queryParams.courier_id ?? null);

  // Sincronizar estados con queryParams
  // Sync states with queryParams
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    if (queryParams.start_date) {
      setStartDate(queryParams.start_date);
    }
  }, [queryParams.start_date]);

  useEffect(() => {
    if (queryParams.end_date) {
      setEndDate(queryParams.end_date);
    }
  }, [queryParams.end_date]);

  useEffect(() => {
    if (queryParams.date_column) {
      setDateColumn(queryParams.date_column as typeof dateColumn);
    }
  }, [queryParams.date_column]);

  useEffect(() => {
    setPaymentMethodId(queryParams.payment_method_id ?? null);
  }, [queryParams.payment_method_id]);

  useEffect(() => {
    setShopId(queryParams.shop_id ?? null);
  }, [queryParams.shop_id]);

  useEffect(() => {
    setCourierId(queryParams.courier_id ?? null);
  }, [queryParams.courier_id]);

  // Convertir filtros a LogisticsFilters
  // Convert filters to LogisticsFilters
  // Solo enviar filtros que el usuario ha seleccionado explícitamente
  // Only send filters that the user has explicitly selected
  // NOTA: La búsqueda es local, no se envía a la API
  // NOTE: Search is local, not sent to API
  const logisticsFilters: LogisticsFilters = useMemo(() => {
    const filters: LogisticsFilters = {};
    const defaultStartDate = getDefaultStartDate();
    const defaultEndDate = getDefaultEndDate();
    const defaultDateColumn: 'processed_at' | 'created_at' | 'updated_at' | 'sent_at' = 'sent_at';

    // Solo enviar fechas si han sido modificadas desde los valores por defecto
    // Only send dates if they have been modified from default values
    if (startDate && startDate !== defaultStartDate) {
      filters.start_date = startDate;
    }
    if (endDate && endDate !== defaultEndDate) {
      filters.end_date = endDate;
    }
    if (dateColumn && dateColumn !== defaultDateColumn) {
      filters.date_column = dateColumn;
    }

    // Solo enviar filtros opcionales si tienen valores seleccionados
    // Only send optional filters if they have selected values
    if (paymentMethodId !== null) {
      filters.payment_method_id = paymentMethodId;
    }
    if (shopId !== null) {
      filters.shop_id = shopId;
    }
    if (courierId !== null) {
      filters.courier_id = courierId;
    }

    // La búsqueda se hace localmente, no se envía a la API
    // Search is done locally, not sent to API

    return filters;
  }, [startDate, endDate, dateColumn, paymentMethodId, shopId, courierId]);

  const { globalMetrics, productMetrics, dailyMetrics, isLoading, isFetching, error } = useLogisticsData({
    filters: logisticsFilters,
  });

  // Mostrar toast si hay error en la API (solo una vez por error)
  // Show toast if there's an API error (only once per error)
  useEffect(() => {
    if (error && error !== lastErrorShown.current) {
      lastErrorShown.current = error;
      toast({
        title: t('toasts.error.title', 'Error'),
        description: error || t('toasts.error.unexpected', 'Error inesperado. Inténtalo de nuevo.'),
        variant: 'destructive',
      });
    }
    // Limpiar el ref cuando el error se resuelve
    // Clear the ref when error is resolved
    if (!error) {
      lastErrorShown.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]); // Solo dependemos de error para evitar loops infinitos

  // 🎯 Marcar que la carga inicial terminó cuando los datos se cargan por primera vez
  // Mark that initial load finished when data loads for the first time
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Adaptar productMetrics a ProductStatistics
  // Adapt productMetrics to ProductStatistics
  const adaptedProductData: ProductStatistics[] = useMemo(() => {
    if (!productMetrics || productMetrics.length === 0) return [];

    return productMetrics.map((metric: ProductMetric, index: number) => ({
      id: index + 1,
      product: {
        id: index + 1,
        name: metric.item_name,
        code: metric.item_code,
        image: metric.item_image || '/imagen/example.webp',
      },
      sent: metric.total_enviados,
      in_transit: {
        count: metric.total_en_transito,
        percentage: metric.porcentaje_en_transito,
      },
      delivered: {
        count: metric.total_entregados,
        percentage: metric.porcentaje_entregado,
      },
      returned: {
        count: metric.total_devueltos,
        percentage: metric.porcentaje_devuelto,
      },
    }));
  }, [productMetrics]);

  // Filtrar datos localmente según el término de búsqueda
  // Filter data locally based on search term
  const filteredProductData: ProductStatistics[] = useMemo(() => {
    if (!searchTerm.trim()) {
      return adaptedProductData;
    }

    const searchLower = searchTerm.trim().toLowerCase();
    return adaptedProductData.filter((item) => {
      // Buscar en nombre y código del producto
      // Search in product name and code
      return (
        item.product.name.toLowerCase().includes(searchLower) || item.product.code.toLowerCase().includes(searchLower)
      );
    });
  }, [adaptedProductData, searchTerm]);

  // Datos mostrados en la tabla (persisten mientras se hace fetching)
  // Data shown in the table (persists while fetching)
  const [displayProductData, setDisplayProductData] = useState<ProductStatistics[]>([]);

  useEffect(() => {
    // Solo actualizar los datos mostrados cuando termina el fetching
    // Only update displayed data when fetching has finished
    if (!isFetching) {
      setDisplayProductData(filteredProductData);
    }
  }, [filteredProductData, isFetching]);

  // Datos mostrados para métricas y gráficos (persisten mientras se hace fetching)
  // Data shown for metrics and charts (persists while fetching)
  const [displayGlobalMetrics, setDisplayGlobalMetrics] = useState<typeof globalMetrics>(undefined);
  const [displayDailyMetrics, setDisplayDailyMetrics] = useState<typeof dailyMetrics>([]);

  // Refs para rastrear los últimos valores y evitar actualizaciones innecesarias
  // Refs to track last values and avoid unnecessary updates
  const lastGlobalMetricsRef = useRef<typeof globalMetrics>(undefined);
  const lastDailyMetricsRef = useRef<typeof dailyMetrics>([]);

  useEffect(() => {
    // Si no está fetching, actualizar con los nuevos datos solo si han cambiado
    // If not fetching, update with new data only if they have changed
    if (!isFetching) {
      if (globalMetrics && globalMetrics !== lastGlobalMetricsRef.current) {
        lastGlobalMetricsRef.current = globalMetrics;
        setDisplayGlobalMetrics(globalMetrics);
      }
      if (dailyMetrics && dailyMetrics !== lastDailyMetricsRef.current) {
        lastDailyMetricsRef.current = dailyMetrics;
        setDisplayDailyMetrics(dailyMetrics);
      }
    }
    // Si es la primera carga y hay datos, actualizar inmediatamente (incluso si está fetching)
    // If it's the first load and there's data, update immediately (even if fetching)
    else if (isInitialLoad) {
      if (globalMetrics && globalMetrics !== lastGlobalMetricsRef.current) {
        lastGlobalMetricsRef.current = globalMetrics;
        setDisplayGlobalMetrics(globalMetrics);
      }
      if (dailyMetrics && dailyMetrics.length > 0 && dailyMetrics !== lastDailyMetricsRef.current) {
        lastDailyMetricsRef.current = dailyMetrics;
        setDisplayDailyMetrics(dailyMetrics);
      }
    }
  }, [globalMetrics, dailyMetrics, isFetching, isInitialLoad]);

  // Handler para búsqueda
  // Search handler
  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (queryParams.search !== value) {
        updateQueryParams({ search: value });
      }
      console.log('dY"? Main - Searching:', value);
    },
    [queryParams.search, updateQueryParams],
  );

  // Handler para cambio de filtros de la barra superior
  // Handler for top bar filters change
  const handleTopBarFiltersChange = useCallback(
    (filters: {
      startDate?: string;
      endDate?: string;
      dateColumn?: 'processed_at' | 'created_at' | 'updated_at' | 'sent_at';
      paymentMethodId?: number | null;
      shopId?: number | null;
      courierId?: number | null;
    }) => {
      const updates: Record<string, any> = {};

      if (filters.startDate !== undefined) {
        setStartDate(filters.startDate);
        updates.start_date = filters.startDate;
      }
      if (filters.endDate !== undefined) {
        setEndDate(filters.endDate);
        updates.end_date = filters.endDate;
      }
      if (filters.dateColumn !== undefined) {
        setDateColumn(filters.dateColumn);
        updates.date_column = filters.dateColumn;
      }
      if (filters.paymentMethodId !== undefined) {
        setPaymentMethodId(filters.paymentMethodId);
        updates.payment_method_id = filters.paymentMethodId;
      }
      if (filters.shopId !== undefined) {
        setShopId(filters.shopId);
        updates.shop_id = filters.shopId;
      }
      if (filters.courierId !== undefined) {
        setCourierId(filters.courierId);
        updates.courier_id = filters.courierId;
      }

      // Actualizar queryParams si hay cambios
      // Update queryParams if there are changes
      if (Object.keys(updates).length > 0) {
        updateQueryParams(updates);
      }
    },
    [updateQueryParams],
  );

  // Columnas para ColumnSelector
  // Columns for ColumnSelector
  const tableColumns: ColumnDef<ProductStatistics>[] = useMemo(
    () => [
      { id: 'product', header: t('table.product') },
      { id: 'sent', header: t('table.sent') },
      { id: 'in_transit', header: t('table.in_transit') },
      { id: 'delivered', header: t('table.delivered') },
      { id: 'returned', header: t('table.returned') },
    ],
    [t],
  );

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.id as string));

  const handleColumnsChange = useCallback((newSelected: string[]) => {
    setSelectedColumns(newSelected);
  }, []);

  // Detectar si hay filtros activos (búsqueda o filtros de la barra superior)
  // Detect if there are active filters (search or top bar filters)
  const hasActiveFilters = useMemo(() => {
    // Búsqueda activa
    // Active search
    if (searchTerm && searchTerm.trim().length > 0) return true;

    // Filtros de la barra superior activos
    // Active top bar filters
    const defaultStartDate = getDefaultStartDate();
    const defaultEndDate = getDefaultEndDate();
    const defaultDateColumn: 'processed_at' | 'created_at' | 'updated_at' | 'sent_at' = 'sent_at';

    // Fechas modificadas desde valores por defecto
    // Dates modified from default values
    if (startDate && startDate !== defaultStartDate) return true;
    if (endDate && endDate !== defaultEndDate) return true;
    if (dateColumn && dateColumn !== defaultDateColumn) return true;

    // Filtros opcionales seleccionados
    // Optional filters selected
    if (paymentMethodId !== null) return true;
    if (shopId !== null) return true;
    if (courierId !== null) return true;

    return false;
  }, [searchTerm, startDate, endDate, dateColumn, paymentMethodId, shopId, courierId]);

  // Detectar si todos los datos están en 0 (solo si NO hay filtros activos)
  // Detect if all data is zero (only if there are NO active filters)
  const isAllDataZero = useMemo(() => {
    // Si hay filtros activos, no mostrar el EmptyView global
    // If there are active filters, don't show global EmptyView
    if (hasActiveFilters) return false;

    // Verificar globalMetrics
    if (globalMetrics) {
      const hasGlobalData =
        globalMetrics.total > 0 ||
        globalMetrics.sent > 0 ||
        globalMetrics.delivered > 0 ||
        globalMetrics.returned > 0 ||
        globalMetrics.in_transit > 0;
      if (hasGlobalData) return false;
    }

    // Verificar productMetrics
    if (productMetrics && productMetrics.length > 0) {
      const hasProductData = productMetrics.some(
        (metric: ProductMetric) =>
          metric.total_enviados > 0 ||
          metric.total_en_transito > 0 ||
          metric.total_entregados > 0 ||
          metric.total_devueltos > 0,
      );
      if (hasProductData) return false;
    }

    // Verificar dailyMetrics
    if (dailyMetrics && dailyMetrics.length > 0) {
      const hasDailyData = dailyMetrics.some(
        (metric: any) =>
          metric.orders_sent > 0 ||
          metric.orders_delivered > 0 ||
          metric.orders_returned > 0 ||
          metric.orders_in_transit > 0 ||
          metric.amount_sent > 0 ||
          metric.amount_delivered > 0 ||
          metric.amount_returned > 0 ||
          metric.amount_in_transit > 0,
      );
      if (hasDailyData) return false;
    }

    // Si llegamos aquí, todos los datos están en 0 o no hay datos
    return true;
  }, [globalMetrics, productMetrics, dailyMetrics, hasActiveFilters]);

  // Mostrar skeleton solo en la carga inicial
  // Show skeleton only on initial load
  if (isLoading && isInitialLoad) {
    return <LogisticsSkeleton />;
  }

  // Detectar si hay error y no hay datos previos
  // Detect if there's an error and no previous data
  const hasErrorNoData = error && !globalMetrics && !productMetrics?.length && !dailyMetrics?.length;
  const hasChartsData = !!displayGlobalMetrics || (!!displayDailyMetrics && displayDailyMetrics.length > 0);

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Barra de filtros superior - Top filters bar (siempre visible) */}
      {/* Top filters bar (always visible) */}
      <LogisticsFiltersBar
        startDate={startDate}
        endDate={endDate}
        dateColumn={dateColumn}
        paymentMethodId={paymentMethodId}
        shopId={shopId}
        courierId={courierId}
        onFiltersChange={handleTopBarFiltersChange}
        getDefaultStartDate={getDefaultStartDate}
        getDefaultEndDate={getDefaultEndDate}
      />

      {/* Si hay error y no hay datos previos, mostrar mensaje de error */}
      {/* If there's an error and no previous data, show error message */}
      {hasErrorNoData ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title', 'Error al cargar datos')}
          description={t(
            'empty.error.description',
            'No se pudieron cargar los datos de logística. Por favor, intenta de nuevo.',
          )}
        />
      ) : isAllDataZero ? (
        /* Mostrar EmptyView si todos los datos están en 0 */
        /* Show EmptyView if all data is zero */
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.no_data.title', 'No hay datos disponibles')}
          description={t(
            'empty.no_data.description',
            'No se encontraron datos de logística para el período seleccionado.',
          )}
        />
      ) : (
        /* Contenedor con opacity durante fetching - Container with opacity during fetching */
        <div
          className={`space-y-6 transition-opacity duration-300 ${
            isFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          {/* Métricas de logística - Logistics metrics */}
          {displayGlobalMetrics && <LogisticsMetrics globalMetrics={displayGlobalMetrics} isLoading={isLoading} />}

          {/* Gráficos de pedidos - Orders charts */}
          {hasChartsData && (
            <OrdersChartsSection
              globalMetrics={displayGlobalMetrics}
              dailyMetrics={displayDailyMetrics}
              isLoading={isLoading}
            />
          )}

          {/* Sección de estadísticas por producto - Product statistics section */}
          <ProductStatisticsSection
            data={displayProductData}
            total={displayProductData.length}
            handleSearch={handleSearch}
            columns={tableColumns}
            selectedColumns={selectedColumns}
            onColumnsChange={handleColumnsChange}
            searchTerm={searchTerm}
            isFetching={isFetching}
          />
        </div>
      )}

      <Separator className="my-8" />

      <section
        className="space-y-3 pt-2 scroll-mt-8"
        aria-labelledby="logistic-pivot-heading"
      >
        <div>
          <h2 id="logistic-pivot-heading" className="text-lg font-semibold text-foreground">
            {t('logistic_pivot_section.title')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{t('logistic_pivot_section.subtitle')}</p>
        </div>
        <LogisticPivotMain queryParamKeyPrefix="lp" embedded />
      </section>
    </div>
  );
};
